// Resolves the post-booking details a guest should see for their ticket:
// the departure wave's value when it is set and non-blank, otherwise the event's.
// Pure (no Deno/npm imports) so the same file is unit-tested from tests/ with bun.

export interface HostLike {
  first_name?: string | null;
  last_name?: string | null;
  bio?: string | null;
}

export interface DepartureGroup {
  id?: string;
  name?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  meeting_venue?: string | null;
  whatsapp_group?: string | null;
  host_id?: string | null;
  host?: HostLike | null;
}

export interface DepartureEvent {
  start_time: string;
  end_time?: string | null;
  venue?: string | null;
  whatsapp_group?: string | null;
  host?: HostLike | null;
}

export interface ResolvedDeparture {
  start_time: string;
  end_time: string | null | undefined;
  venue: string | null | undefined;
  /** Wave meeting venue only (null when the wave has none / no wave). */
  meeting_venue: string | null;
  whatsapp_group: string | null | undefined;
  host: HostLike | null | undefined;
  group_name: string | null;
}

/** Non-blank string, or undefined (empty and whitespace-only fall through). */
export const filled = (v: string | null | undefined): string | undefined =>
  typeof v === "string" && v.trim() !== "" ? v : undefined;

export function resolveDeparture(
  event: DepartureEvent,
  group: DepartureGroup | null | undefined,
): ResolvedDeparture {
  if (!group) {
    return {
      start_time: event.start_time,
      end_time: event.end_time,
      venue: event.venue,
      meeting_venue: null,
      whatsapp_group: event.whatsapp_group,
      host: event.host,
      group_name: null,
    };
  }
  const hasWaveHost = !!group.host_id && !!group.host;
  return {
    start_time: filled(group.start_time) ?? event.start_time,
    end_time: filled(group.end_time) ?? event.end_time,
    venue: filled(group.meeting_venue) ?? event.venue,
    meeting_venue: filled(group.meeting_venue) ?? null,
    whatsapp_group: filled(group.whatsapp_group) ?? event.whatsapp_group,
    host: hasWaveHost ? group.host : event.host,
    group_name: filled(group.name ?? undefined) ?? null,
  };
}

export const hostFullName = (h: HostLike | null | undefined): string =>
  h ? `${h.first_name || ""} ${h.last_name || ""}`.trim() : "";

/** Calendar entry title: event title, plus " (wave name)" for grouped tickets. */
export const calendarTitle = (title: string, groupName?: string | null): string => {
  const name = filled(groupName ?? undefined);
  return name ? `${title} (${name.trim()})` : title;
};

/**
 * Google Calendar "create event" link. Dates stay UTC (toISOString + Z).
 * Pass the resolved `groupName` so a wave booking's entry is named after its wave —
 * a link can only create entries, so after a wave move the old one must be distinguishable.
 */
export function googleCalendarLink(opts: {
  title: string;
  start: string;
  end: string;
  details?: string | null;
  location?: string | null;
  groupName?: string | null;
}): string {
  const fmt = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0];
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calendarTitle(opts.title, opts.groupName))}&dates=${fmt(opts.start)}Z/${fmt(opts.end)}Z&details=${encodeURIComponent(opts.details || "")}&location=${encodeURIComponent(opts.location || "")}`;
}

/** Google Maps search link for a meeting point ("" when there is none). */
export const mapsSearchLink = (place: string | null | undefined): string => {
  const p = filled(place);
  return p ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.trim())}` : "";
};

// ---- Agenda time shifting -------------------------------------------------
// Matches "2:00-2:30PM" (shared meridiem), "2:00 PM – 2:30 PM", and single "4:30PM".
const MER = String.raw`(?:a\.?m\.?|p\.?m\.?)`;
const CLOCK = String.raw`(1[0-2]|0?[1-9]):([0-5]\d)`;
const TOKEN_PATTERN = new RegExp(
  String.raw`\b${CLOCK}(\s*)(${MER})?(?:(\s*[-–—]\s*)${CLOCK}(\s*)(${MER}))?(?![\w])`,
  "gi",
);
const ANY_CLOCK = /\b\d{1,2}:\d{2}\b/;

const isPm = (m: string) => m.toLowerCase().startsWith("p");
const to24 = (h: number, pm: boolean) => (h % 12) + (pm ? 12 : 0);
/** Re-spell a meridiem keeping the original's case and dots, flipping a/p if needed. */
const respell = (orig: string, pm: boolean) => {
  const letter = orig[0] === orig[0].toUpperCase() ? (pm ? "P" : "A") : (pm ? "p" : "a");
  return letter + orig.slice(1);
};
const shiftMinutes = (h24: number, min: number, offset: number) => {
  const t = (((h24 * 60 + min + offset) % 1440) + 1440) % 1440;
  const H = Math.floor(t / 60);
  return { h: H % 12 || 12, m: String(t % 60).padStart(2, "0"), pm: H >= 12 };
};

/** Shift one text run; returns null if any clock in it could not be confidently shifted. */
const shiftText = (text: string, offset: number): string | null => {
  let ok = true;
  const out = text.replace(TOKEN_PATTERN, (match, h1, m1, sp1, mer1, dash, h2, m2, sp2, mer2) => {
    if (!dash) {
      if (!mer1) { ok = false; return match; } // bare "2:00" — meridiem unknown
      const s = shiftMinutes(to24(+h1, isPm(mer1)), +m1, offset);
      return `${s.h}:${s.m}${sp1}${respell(mer1, s.pm)}`;
    }
    const endPm = isPm(mer2);
    // Shared meridiem: start takes the end's meridiem unless it is later on the clock (11:30-12:30PM → AM).
    const startPm = mer1 ? isPm(mer1) : ((+h1 % 12) <= (+h2 % 12) ? endPm : !endPm);
    const s = shiftMinutes(to24(+h1, startPm), +m1, offset);
    const e = shiftMinutes(to24(+h2, endPm), +m2, offset);
    const startMer = mer1 ? `${sp1}${respell(mer1, s.pm)}` : (s.pm !== e.pm ? `${sp2}${respell(mer2, s.pm)}` : sp1);
    return `${s.h}:${s.m}${startMer}${dash}${e.h}:${e.m}${sp2}${respell(mer2, e.pm)}`;
  });
  if (!ok) return null;
  // Any clock left unconsumed would be a half-shift: refuse.
  const leftover = text.replace(TOKEN_PATTERN, "");
  return ANY_CLOCK.test(leftover) ? null : out;
};

/**
 * Shift visible agenda clock labels by the departure's offset from the event start.
 * Only text outside HTML tags is touched. All-or-nothing: if any clock can't be
 * confidently shifted, the whole agenda is returned unchanged (never half-shifted).
 */
export const shiftAgendaTimes = (agenda: string, eventStart: string, groupStart?: string | null): string => {
  if (!agenda || !groupStart) return agenda;
  const offset = Math.round((new Date(groupStart).getTime() - new Date(eventStart).getTime()) / 60000);
  if (!Number.isFinite(offset) || offset === 0) return agenda;
  const parts = agenda.split(/(<[^>]*>)/);
  const out: string[] = [];
  for (const part of parts) {
    if (part.startsWith("<")) { out.push(part); continue; }
    const shifted = shiftText(part, offset);
    if (shifted === null) return agenda;
    out.push(shifted);
  }
  return out.join("");
};
